import { Controller, useForm, useFieldArray } from "react-hook-form"
import { useEffect } from "react"
import Modal from "../../UI/Modal/Modal"
import classes from "../AdminForm.module.css"
import CustomButton from "../../UI/CustomButton/CustomButton"
import Input from "../../UI/Form/Input"
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd"
import { howItWasApi } from "../../../Api/api"
import { Button } from "@mui/material"

const EditHowItWas = ({ onClose, initialData, updateHowItWas, setIsFetching }) => {
    // 1) инициализируем форму с начальным объектом
    const { handleSubmit, control, reset, getValues } = useForm({
        defaultValues: {
            title: initialData.title,
            backgroundImg: initialData.backgroundImg,
            content: initialData.content.map((item) => ({
                title: item.title,
                photo: item.photo,
                youtubeUrl: item.youtubeUrl
            }))
        }
    })

    // если initialData может приходить асинхронно, ресетим форму при обновлении
    useEffect(() => {
        reset({
            title: initialData.title,
            backgroundImg: initialData.backgroundImg,
            content: initialData.content.map((item) => ({
                title: item.title,
                photo: item.photo,
                youtubeUrl: item.youtubeUrl
            }))
        })
    }, [initialData, reset])

    const { fields, append, remove, move } = useFieldArray({
        control,
        name: "content"
    })

    const onDragEnd = (result) => {
        if (!result.destination) return
        move(result.source.index, result.destination.index)
    }

    async function uploadFile(file) {
        const res = await howItWasApi.uploadFile(file)
        return res.path
    }

    async function updateFile(file, oldUrl) {
        const res = await howItWasApi.updateFile(file, oldUrl)
        return res.path
    }

    async function deleteFileUrl(fileUrl) {
        await howItWasApi.deleteFile(fileUrl)
    }

    // 2) при удалении слайда — чистим файл, если это строковый url
    const handleRemove = async (index) => {
        const item = getValues(`content.${index}`)
        if (item.photo && typeof item.photo === "string") {
            try {
                await deleteFileUrl(item.photo)
            } catch (e) {
                console.warn("Не удалось удалить старый файл:", e)
            }
        }
        remove(index)
    }

    const onSubmit = async (data) => {
        setIsFetching(true)

        // 3) backgroundImg
        let bgUrl = data.backgroundImg
        if (data.backgroundImg instanceof File) {
            bgUrl = initialData.backgroundImg
                ? await updateFile(data.backgroundImg, initialData.backgroundImg)
                : await uploadFile(data.backgroundImg)
        }

        // 4) контент
        const content = await Promise.all(
            data.content.map(async (item, idx) => {
                // если загрузили новый файл
                if (item.photo instanceof File) {
                    const oldUrl =
                        initialData.content[idx] && typeof initialData.content[idx].photo === "string"
                            ? initialData.content[idx].photo
                            : null
                    const newUrl = oldUrl ? await updateFile(item.photo, oldUrl) : await uploadFile(item.photo)
                    return {
                        title: item.title,
                        photo: newUrl,
                        youtubeUrl: item.youtubeUrl || ""
                    }
                }
                // если photo остался строкой или пуст
                return {
                    title: item.title,
                    photo: item.photo || "",
                    youtubeUrl: item.youtubeUrl || ""
                }
            })
        )

        const payload = {
            title: data.title,
            backgroundImg: bgUrl,
            content
        }

        updateHowItWas(initialData._id, payload)
        setIsFetching(false)
    }

    return (
        <Modal
            title={"Edit How It Was"}
            onClose={onClose}
        >
            <form
                className={classes.main}
                onSubmit={handleSubmit(onSubmit)}
            >
                <Controller
                    name="title"
                    control={control}
                    rules={{ required: "Required Field!" }}
                    render={({ field, fieldState: { error } }) => (
                        <Input
                            {...field}
                            value={field.value}
                            onChange={field.onChange}
                            error={error}
                            label="Name"
                        />
                    )}
                />

                {/* Background */}
                <Controller
                    name="backgroundImg"
                    control={control}
                    render={({ field }) => (
                        <>
                            <h4>Background Img</h4>
                            <input
                                type="file"
                                accept="image/*"
                                id="bg"
                                hidden
                                onChange={(e) => field.onChange(e.target.files[0])}
                            />
                            <label
                                className={classes.selectPhotoDrag}
                                htmlFor="bg"
                            >
                                Select Photo
                            </label>
                            {/* preview */}
                            {field.value &&
                                (field.value instanceof File ? (
                                    <img
                                        src={URL.createObjectURL(field.value)}
                                        alt="preview"
                                        width={120}
                                    />
                                ) : (
                                    <img
                                        src={field.value}
                                        alt="preview"
                                        width={120}
                                    />
                                ))}
                        </>
                    )}
                />

                <h4>Content</h4>
                <CustomButton
                    type="button"
                    onClick={() => append({ title: "", photo: null, youtubeUrl: "" })}
                >
                    Add Slide
                </CustomButton>

                <DragDropContext onDragEnd={onDragEnd}>
                    <Droppable droppableId="content">
                        {(provided) => (
                            <div
                                {...provided.droppableProps}
                                ref={provided.innerRef}
                                className={classes.drag}
                            >
                                {fields.map((item, index) => (
                                    <Draggable
                                        key={item.id}
                                        draggableId={item.id}
                                        index={index}
                                    >
                                        {(prov) => (
                                            <div
                                                ref={prov.innerRef}
                                                {...prov.draggableProps}
                                                className={classes.dragItem}
                                                style={{ ...prov.draggableProps.style }}
                                            >
                                                <span
                                                    {...prov.dragHandleProps}
                                                    style={{ cursor: "grab" }}
                                                >
                                                    ☰
                                                </span>

                                                <div className={classes.dragField}>
                                                    <Controller
                                                        name={`content.${index}.title`}
                                                        control={control}
                                                        render={({ field, fieldState: { error } }) => (
                                                            <Input
                                                                {...field}
                                                                value={field.value}
                                                                onChange={field.onChange}
                                                                error={error}
                                                                label="Slide title"
                                                            />
                                                        )}
                                                    />

                                                    <Controller
                                                        name={`content.${index}.youtubeUrl`}
                                                        control={control}
                                                        render={({ field }) => (
                                                            <Input
                                                                {...field}
                                                                value={field.value}
                                                                onChange={field.onChange}
                                                                label="YouTube URL"
                                                            />
                                                        )}
                                                    />
                                                </div>

                                                {/* Фото */}
                                                <Controller
                                                    name={`content.${index}.photo`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <>
                                                            <input
                                                                type="file"
                                                                accept="image/*"
                                                                hidden
                                                                id={`photo-${index}`}
                                                                onChange={(e) => field.onChange(e.target.files[0])}
                                                            />
                                                            <label
                                                                className={classes.selectPhotoDrag}
                                                                htmlFor={`photo-${index}`}
                                                            >
                                                                Select Photo
                                                            </label>
                                                            {field.value &&
                                                                (field.value instanceof File ? (
                                                                    <img
                                                                        src={URL.createObjectURL(field.value)}
                                                                        alt="preview"
                                                                        className={classes.dragPreview}
                                                                    />
                                                                ) : (
                                                                    <img
                                                                        src={field.value}
                                                                        alt="preview"
                                                                        className={classes.dragPreview}
                                                                    />
                                                                ))}
                                                        </>
                                                    )}
                                                />

                                                <Button
                                                    type="button"
                                                    onClick={() => handleRemove(index)}
                                                    className={classes.removeDragButt}
                                                >
                                                    ×
                                                </Button>
                                            </div>
                                        )}
                                    </Draggable>
                                ))}
                                {provided.placeholder}
                            </div>
                        )}
                    </Droppable>
                </DragDropContext>

                <CustomButton type="submit">Update</CustomButton>
            </form>
        </Modal>
    )
}

export default EditHowItWas
